import React, { useEffect, useId, useState } from 'react';
import { X } from 'lucide-react';
import { Reveal } from '../Reveal';
import { SectionBand, Eyebrow } from './SectionBand';
import { ProofFrame } from './ProofFrame';
import { trackProofLightboxOpen } from '../../lib/analytics';
import { proofAssets } from '../../lib/proofAssets';

/**
 * Absolute CDN URLs — Mag Patterns cannot serve Vite-imported image binaries.
 * Stage 1 uses the tall still + CSS scrollReveal for motion.
 */
const HOME_FULL_SRC = proofAssets.homeFull;
const stages = [{
  n: '1',
  id: 'homepage',
  title: 'Give your book a place to keep working',
  caption: 'Use it as the destination behind podcasts, speaking, content, referrals, and the book itself—so interested readers have somewhere useful to go next.',
  src: HOME_FULL_SRC,
  staticSrc: HOME_FULL_SRC,
  lightboxSrc: HOME_FULL_SRC,
  chromeLabel: 'Simplify to Amplify · Reader home',
  lightboxEyebrow: 'Give your book a place to keep working',
  lightboxHeadline: 'The book becomes a branded destination readers can return to, explore, and act on.',
  objectPosition: 'center top',
  scrollReveal: true,
  eager: false,
  emphasize: false
}, {
  n: '2',
  id: 'diagnostic',
  title: 'Help readers find where your ideas apply',
  caption: 'Turn your framework into a diagnostic that helps readers recognize themselves in the work—and shows you what matters to them.',
  src: proofAssets.diagnosticCrop,
  lightboxSrc: proofAssets.diagnosticFull,
  chromeLabel: 'Simplify to Amplify · Find what applies',
  lightboxEyebrow: 'Help readers find where your ideas apply',
  lightboxHeadline: 'The reader stops skimming and starts recognizing their own situation.',
  objectPosition: 'center top',
  scrollReveal: false,
  eager: false,
  emphasize: false
}, {
  n: '3',
  id: 'readout',
  title: 'Make the ideas personal',
  caption: 'Give each reader an interpretation of the book based on their situation, pattern, and most useful next move.',
  src: proofAssets.readout,
  lightboxSrc: proofAssets.readout,
  chromeLabel: 'Simplify to Amplify · Personalized readout',
  lightboxEyebrow: 'Make the ideas personal',
  lightboxHeadline: 'The book’s ideas become specific to what this reader is experiencing.',
  objectPosition: '28% 40%',
  scrollReveal: false,
  eager: false,
  emphasize: false
}, {
  n: '4',
  id: 'next_move',
  title: 'Turn insight into action',
  caption: 'Help readers save a goal, success signal, next move, and support need they can use and return to.',
  src: proofAssets.nextMoveCrop,
  lightboxSrc: proofAssets.nextMoveFull,
  chromeLabel: 'Simplify to Amplify · Next move',
  lightboxEyebrow: 'Turn insight into action',
  lightboxHeadline: 'The reader leaves with a concrete move instead of another unfinished idea.',
  objectPosition: 'center top',
  scrollReveal: false,
  eager: false,
  emphasize: false
}, {
  n: '5',
  id: 'hand_raise',
  title: 'Let serious readers raise their hand with context',
  caption: 'When a reader asks for help, their pattern, saved move, and stated interest come with them—so the inquiry arrives with context, not as a blank contact form.',
  src: proofAssets.requestGuidance,
  lightboxSrc: proofAssets.requestGuidance,
  chromeLabel: 'Simplify to Amplify · Request guidance',
  lightboxEyebrow: 'Raise a hand with context',
  lightboxHeadline: 'A serious reader can ask for help without starting over—and the next conversation begins with context.',
  objectPosition: '50% 22%',
  scrollReveal: false,
  eager: false,
  emphasize: true
}] as const;
type Stage = (typeof stages)[number];
function ProofLightbox({
  stage,
  onClose
}: {
  stage: Stage;
  onClose: () => void;
}) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/80 p-4 backdrop-blur-sm md:p-8" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={onClose} data-id="element-880" data-mp="2qfiuamzmscqr">
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-paper shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]" onClick={e => e.stopPropagation()} data-id="element-881" data-mp="2zggyp6dzem9v">
        <div className="sticky top-0 z-10 flex flex-shrink-0 items-start justify-between gap-4 border-b border-gray-200/80 bg-paper px-5 py-5 md:px-6 md:py-6" data-id="element-882" data-mp="26rrwf416wawj">
          <div className="min-w-0 max-w-3xl pr-2" data-id="element-883" data-mp="2bpbmr2b5we4u">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-884" data-mp="2ghbi0hgaeiv3">
              {stage.lightboxEyebrow}
            </p>
            <p id={titleId} className="mt-2.5 font-serif text-xl leading-snug text-seaglass md:text-[1.65rem]" data-id="element-885" data-mp="1j1o2xphptrj9">
              {stage.lightboxHeadline}
            </p>
          </div>
          <button type="button" onClick={onClose} className="sticky top-0 flex-shrink-0 rounded-full p-2 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close preview" data-id="element-886" data-mp="1gggeynu3y48u">
            <X className="h-5 w-5" data-id="element-887" data-mp="12ia06q0uouz1" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-paper px-3 py-3 md:px-5 md:py-5" data-id="element-888" data-mp="12hqdkw099ieo">
          <img src={stage.lightboxSrc} alt={`Simplify to Amplify — ${stage.title}`} className="mx-auto block h-auto w-full max-w-full rounded-lg" data-id="element-889" data-mp="xkqbew6j0zsu" />
        </div>
      </div>
    </div>;
}
export function PrototypeProof() {
  const [active, setActive] = useState<Stage | null>(null);
  const openStage = (stage: Stage) => {
    trackProofLightboxOpen(stage.n, stage.id);
    setActive(stage);
  };
  return <SectionBand tone="paperDark" width="wide" className="overflow-x-clip" data-id="element-890" data-mp="eu8l0y962gan">
      <div className="max-w-3xl" data-id="element-891" data-mp="9tuke19wa8mn">
        <Reveal data-id="element-892" data-mp="5qgfljy9qyd">
          <Eyebrow data-id="element-893" data-mp="576ar0jk6uw">Live client build</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl leading-tight text-navy md:text-5xl" data-id="element-894" data-mp="4fapg6y294m">
            See what your book could become.
          </h2>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-navy/55" data-id="element-895" data-mp="3mdgjzq13em4y">
            Simplify to Amplify — Reader-to-Revenue experience
          </p>
          <p className="mt-5 text-lg leading-relaxed text-text-muted" data-id="element-896" data-mp="3rdut7befk24k">
            A branded reader experience gives your book a place to keep
            working—helping readers use your ideas, revealing what they need,
            and making it easier for the right people to move toward the right
            offer.
          </p>
        </Reveal>
      </div>

      <div className="mt-10 flex flex-col gap-12 md:mt-12 md:gap-14" data-id="element-897" data-mp="e46uxyo20lr2">
        {stages.map((stage, i) => {
        const imageLeft = i % 2 === 1;
        const gridClass = stage.emphasize ? imageLeft ? 'md:grid-cols-[1.3fr_0.8fr]' : 'md:grid-cols-[0.8fr_1.3fr]' : imageLeft ? 'md:grid-cols-[1.15fr_0.9fr]' : 'md:grid-cols-[0.9fr_1.15fr]';
        return <Reveal key={stage.n} delay={0.05} data-id="element-898" data-mp="2md4p4lerhhjj" data-mpi={i}>
              <div className={`grid items-center gap-5 md:gap-10 ${gridClass}`} data-id="element-899" data-mp="2mcbq66m1nwby">
                <div className={`min-w-0 ${imageLeft ? 'md:order-2' : 'md:order-1'}`} data-id="element-900" data-mp="304rncqs9l04y">
                  <div className="flex items-center gap-4" data-id="element-901" data-mp="2w2ei03hfj48p">
                    <span className={`flex flex-shrink-0 items-center justify-center rounded-full font-serif text-lg ${stage.emphasize ? 'h-12 w-12 bg-sand text-navy shadow-[0_10px_24px_-12px_rgba(26,40,56,0.55)]' : 'h-11 w-11 bg-seaglass text-white'}`} data-id="element-902" data-mp="2w50ywtq7mw3l">
                      {stage.n}
                    </span>
                    <h3 className={`min-w-0 font-serif text-navy ${stage.emphasize ? 'text-2xl md:text-[2rem]' : 'text-2xl md:text-3xl'}`} data-id="element-903" data-mp="2cexa484199k6">
                      {stage.title}
                    </h3>
                  </div>
                  <p className={`mt-4 max-w-md leading-relaxed text-text-muted ${stage.emphasize ? 'text-lg md:text-xl' : 'text-lg'}`} data-id="element-904" data-mp="3nulzf64reko5">
                    {stage.caption}
                  </p>
                </div>
                <div className={`min-w-0 ${imageLeft ? 'md:order-1' : 'md:order-2'}`} data-id="element-905" data-mp="gqyvyofcdn3o">
                  <button type="button" onClick={() => openStage(stage)} className="group w-full max-w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass focus-visible:ring-offset-0" aria-label={`Enlarge: ${stage.title}`} data-id="element-906" data-mp="kr915yvb89hh">
                    <span className="block transition-transform duration-300 group-hover:-translate-y-0.5" data-id="element-907" data-mp="ujer73204gu1">
                      <ProofFrame src={stage.src} staticSrc={'staticSrc' in stage ? stage.staticSrc : undefined} alt={`Simplify to Amplify — ${stage.title}`} chromeLabel={stage.chromeLabel} objectPosition={stage.objectPosition} loading={stage.eager ? 'eager' : 'lazy'} emphasize={stage.emphasize} scrollReveal={stage.scrollReveal} data-id="element-908" data-mp="zi87t4cl4nyf" />
                    </span>
                    <span className="mt-3 block text-center text-sm font-medium text-navy/55 transition-colors group-hover:text-navy/80" data-id="element-909" data-mp="3a981nfq3i96n">
                      Click to enlarge
                    </span>
                  </button>
                </div>
              </div>
            </Reveal>;
      })}
      </div>

      {active && <ProofLightbox stage={active} onClose={() => setActive(null)} data-id="element-910" data-mp="2szdycccv1u50" />}
    </SectionBand>;
}